import React, { useEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";
import PCCARE_LOGO from "../../assests/images/Bgpccare.png";
import { getProductEnquiryLink } from "../../utils/whatsapp";
import { getRequest } from "../../apis/ApiInstence";
import { cachedAxiosGet } from "../../utils/apiCache";

gsap.registerPlugin(ScrollTrigger);

// 🔹 helper: get cart from localStorage
const getCart = () => JSON.parse(localStorage.getItem("cart")) || [];

const getAssetBaseUrl = () => {
  const rawBase = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND ||
    "http://localhost:8000"
  ).replace(/\/$/, "");

  return rawBase.replace(/\/api\/v1$/, "");
};

const normalizeProductsArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.data?.products)) return payload.data.products;
  return [];
};

const normalizeGroupsObject = (payload) => {
  const obj = payload?.data && typeof payload?.data === "object" ? payload.data : payload;
  if (!obj || Array.isArray(obj) || typeof obj !== "object") return [];

  return Object.entries(obj)
    .map(([key, products]) => ({
      key,
      products: Array.isArray(products) ? products : [],
    }))
    .filter((g) => g.key && g.products.length > 0)
    .sort((a, b) => String(a.key).localeCompare(String(b.key)));
};

const LATEST_COUNT = 4;

const NewArrival = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef([]);
  const navigate = useNavigate();

  const assetBaseUrl = useMemo(() => getAssetBaseUrl(), []);

  const [mode, setMode] = useState("brand"); // "brand" | "screen"
  const [activeGroupKey, setActiveGroupKey] = useState("");

  const [groupsByBrand, setGroupsByBrand] = useState([]);
  const [groupsByScreen, setGroupsByScreen] = useState([]);
  const [fallbackProducts, setFallbackProducts] = useState([]);
  const [groupProductsCache, setGroupProductsCache] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cartCount, setCartCount] = useState(getCart().length);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  /* ================= FETCH PRODUCTS ================= */
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [brandRes, screenRes] = await Promise.allSettled([
          cachedAxiosGet(
            "latest-by-brand",
            () => getRequest("/products/latest-by-brand"),
            120000,
          ),
          cachedAxiosGet(
            "latest-by-screen-size",
            () => getRequest("/products/latest-by-screen-size"),
            120000,
          ),
        ]);

        const brandGroups =
          brandRes.status === "fulfilled" ? normalizeGroupsObject(brandRes.value?.data) : [];
        const screenGroups =
          screenRes.status === "fulfilled" ? normalizeGroupsObject(screenRes.value?.data) : [];

        if (cancelled) return;

        setGroupsByBrand(brandGroups);
        setGroupsByScreen(screenGroups);

        if (brandGroups.length > 0) {
          setMode("brand");
          setActiveGroupKey(brandGroups[0].key);
          setFallbackProducts([]);
          return;
        }

        if (screenGroups.length > 0) {
          setMode("screen");
          setActiveGroupKey(screenGroups[0].key);
          setFallbackProducts([]);
          return;
        }

        const fallback = await cachedAxiosGet(
          `products:latest:${LATEST_COUNT}`,
          () =>
            getRequest("/products", {
              params: { status: "active", limit: LATEST_COUNT },
            }),
          120000,
        );

        if (cancelled) return;
        setFallbackProducts(normalizeProductsArray(fallback?.data));
      } catch (e) {
        if (cancelled) return;
        setError("Products couldn't be loaded. Please refresh the page.");
        setFallbackProducts([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ================= CART UPDATE ================= */
  useEffect(() => {
    window.dispatchEvent(new Event("cartUpdated"));
  }, [cartCount]);

  const currentGroups = mode === "screen" ? groupsByScreen : groupsByBrand;

  // Fetch up to 5 products for the selected group (backend latest-by-* returns 4 only)
  useEffect(() => {
    let cancelled = false;

    const key = String(activeGroupKey || "");
    if (!key) return;

    const cacheKey = `${mode}:${key}`;
    if (groupProductsCache[cacheKey]?.length) return;

    const load = async () => {
      try {
        const filterParam = mode === "screen" ? "screen_size" : "brand";
        const res = await cachedAxiosGet(
          `products:${filterParam}:${key}:${LATEST_COUNT}`,
          () =>
            getRequest("/products", {
              params: { status: "active", limit: LATEST_COUNT, [filterParam]: key },
            }),
          120000,
        );

        if (cancelled) return;
        const arr = normalizeProductsArray(res?.data);
        setGroupProductsCache((prev) => ({ ...prev, [cacheKey]: arr }));
      } catch {
        // Ignore; fallback to whatever we already have from latest-by-* response.
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [activeGroupKey, groupProductsCache, mode]);

  const products = useMemo(() => {
    if (currentGroups.length > 0) {
      const group = currentGroups.find((g) => String(g.key) === String(activeGroupKey));
      const cacheKey = `${mode}:${String(activeGroupKey || "")}`;
      const fromCache = groupProductsCache[cacheKey];
      const list = Array.isArray(fromCache) && fromCache.length ? fromCache : group?.products || [];
      return list.slice(0, LATEST_COUNT);
    }
    return (fallbackProducts || []).slice(0, LATEST_COUNT);
  }, [activeGroupKey, currentGroups, fallbackProducts, groupProductsCache, mode]);

  /* ================= GSAP ANIMATION ================= */
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: -40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: sectionRef.current, start: "top 85%" },
          },
        );
      }

      if (cardsRef.current?.length) {
        gsap.fromTo(
          cardsRef.current,
          { opacity: 0, y: 80, scale: 0.94 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.9,
            stagger: 0.15,
            ease: "power3.out",
            scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
          },
        );
      }

      ScrollTrigger.refresh();
    }, sectionRef);

    return () => ctx.revert();
  }, [mode, activeGroupKey, products.length]);

  /* ================= ADD TO CART ================= */
  const showMessage = (msg) => {
    setToastMsg(msg);
    setShowToast(true);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setShowToast(false), 2200);
  };

  const handleAddToCart = (product) => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const exists = cart.some((item) => String(item.id) === String(product.id));
    if (exists) {
      showMessage("Already added in booking");
      return;
    }

    const updated = [
      ...cart,
      { ...product, item_type: "product", quantity: 1 },
    ];

    localStorage.setItem("cart", JSON.stringify(updated));
    setCartCount(updated.length);
    showMessage("Added to booking");
  };

  /* ================= IMAGE COMPONENT ================= */
 const ProductImage = ({ images }) => {
  let imageArray = [];

  try {
    imageArray = Array.isArray(images) ? images : images ? JSON.parse(images) : [];
  } catch {
    imageArray = [];
  }

  // 👇 Sirf pehli 3 images use karenge
  const limitedImages = imageArray.slice(0, 3);

  const [index, setIndex] = useState(0);
  const intervalRef = useRef(null);

  const handleMouseEnter = () => {
    if (limitedImages.length > 1) {
      intervalRef.current = setInterval(() => {
        setIndex((prev) => (prev + 1) % limitedImages.length);
      }, 1000);
    }
  };

  const handleMouseLeave = () => {
    clearInterval(intervalRef.current);
    setIndex(0);
  };

    return (
      <div
      className="w-full h-28 sm:h-36 md:h-44 mb-2 overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
      <img
        src={
          limitedImages.length > 0
            ? `${assetBaseUrl}/uploads/${limitedImages[index]}`
            : PCCARE_LOGO
        }
        alt="product"
        className="w-full h-full object-contain transition duration-300"
      />
    </div>
  );
};

  cardsRef.current = [];

  return (
   <section ref={sectionRef} className="w-full lg:py-16 py-8 bg-[#f8faff]">
  <div className="max-w-7xl mx-auto px-4">
    <div
      ref={headingRef}
      className="lg:mb-5 mb-2 flex flex-nowrap items-center justify-between gap-3"
    >
      <div className="text-left min-w-0">
        <h2 className="truncate whitespace-nowrap text-2xl sm:text-4xl font-extrabold text-[#351F72] tracking-tight leading-tight">
          Best Deals
        </h2>
        {/* <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
          Latest products by {mode === "screen" ? "screen size" : "brand"}
        </p> */}
      </div>
 <button
   onClick={() => navigate("/products")}
  className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-[#1F3C88] text-xs sm:text-sm font-semibold transition-all duration-300 pt-1"
 >
  <span className="relative">
    View More
    <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-[#1F3C88] transition-all duration-300 group-hover:w-0"></span>
  </span>

  <i
    className="fas fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1"
    aria-hidden="true"
  ></i>
</button>
    </div>

    {(groupsByBrand.length > 0 || groupsByScreen.length > 0) && (
      <div className="mb-6 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          {groupsByBrand.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setMode("brand");
                if (groupsByBrand[0]) setActiveGroupKey(groupsByBrand[0].key);
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                mode === "brand"
                  ? "bg-[#1F3C88] text-white border-[#1F3C88]"
                  : "bg-white text-[#1F3C88] border-[#cfd9ff] hover:bg-[#eef3ff]"
              }`}
            >
              By Brand
            </button>
          )}

          {groupsByScreen.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setMode("screen");
                if (groupsByScreen[0]) setActiveGroupKey(groupsByScreen[0].key);
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                mode === "screen"
                  ? "bg-[#1F3C88] text-white border-[#1F3C88]"
                  : "bg-white text-[#1F3C88] border-[#cfd9ff] hover:bg-[#eef3ff]"
              }`}
            >
              By Screen Size
            </button>
          )}
        </div>

        {currentGroups.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {currentGroups.slice(0, 10).map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setActiveGroupKey(g.key)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  String(activeGroupKey) === String(g.key)
                    ? "bg-[#351F72] text-white border-[#351F72]"
                    : "bg-white text-[#351F72] border-[#e3e9ff] hover:bg-[#f1f4ff]"
                }`}
              >
                {g.key}
              </button>
            ))}
          </div>
        )}
      </div>
    )}

    {error && (
      <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )}

    {isLoading && products.length === 0 ? (
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:px-0 md:pb-0 md:overflow-visible md:snap-none md:grid md:grid-cols-3 lg:grid-cols-4 md:gap-8">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div
            key={idx}
            className="bg-white rounded-2xl p-3 sm:p-4 shadow border border-[#e3e9ff] animate-pulse w-[160px] sm:w-[220px] md:w-full flex-none snap-start"
          >
            <div className="h-28 sm:h-36 md:h-44 bg-gray-100 rounded-xl mb-4" />
            <div className="h-4 bg-gray-100 rounded w-5/6 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-2/3" />
          </div>
        ))}
      </div>
    ) : products.length === 0 ? (
      <div className="rounded-xl border border-[#e3e9ff] bg-white p-6 text-sm text-gray-600">
        No products found.
      </div>
    ) : (
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:px-0 md:pb-0 md:overflow-visible md:snap-none md:grid md:grid-cols-3 lg:grid-cols-4 md:gap-6 lg:gap-5">
        {products.map((p, i) => {
          const newPrice = Number(p.selling_price || 0);
          const oldPrice = Number(p.original_price || 0);
          const off =
            oldPrice > 0
              ? Math.max(0, Math.round(((oldPrice - newPrice) / oldPrice) * 100))
              : 0;
          const save = oldPrice > 0 ? Math.max(0, oldPrice - newPrice) : 0;

          return (
            <div
              key={p.id ?? i}
              ref={(el) => {
                if (el) cardsRef.current[i] = el;
              }}
              className="group bg-white rounded-2xl p-3 sm:p-4 shadow hover:shadow-2xl transition border border-[#e3e9ff] relative hover:-translate-y-1 duration-300 w-[160px] sm:w-[220px] md:w-full flex-none snap-start"
            >
              <img
                src={PCCARE_LOGO}
                alt="PC Care Logo"
                className="absolute top-2 left-2 sm:top-3 sm:left-3 w-8 h-8 sm:w-10 sm:h-10 object-contain z-30 drop-shadow-md"
              />

              {off > 0 && (
                <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gradient-to-r from-[#F36F21] to-[#E63946] text-white text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full font-bold shadow z-30">
                  {off}% OFF
                </div>
              )}

              <ProductImage images={p.images} />

              <h3 className="text-xs sm:text-sm font-semibold text-[#1F3C88] leading-snug line-clamp-2">
                {p.name}
              </h3>

              <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                <span className="text-yellow-500">★</span>
                <span>{Number(p.rating || 0) || 0}</span>
              </div>

              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg sm:text-2xl font-extrabold text-[#E63946]">
                    ₹{Number.isFinite(newPrice) ? newPrice.toLocaleString() : "0"}
                  </span>
                  {oldPrice > 0 && (
                    <span className="text-xs sm:text-sm line-through text-gray-400">
                      ₹{Number.isFinite(oldPrice) ? oldPrice.toLocaleString() : "0"}
                    </span>
                  )}
                </div>
                {save > 0 && (
                  <p className="hidden sm:block text-xs text-[#F36F21] mt-1 font-medium">
                    You save ₹{save.toLocaleString()}
                  </p>
                )}
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => {
                    handleAddToCart(p);
                    navigate(`/products2/${p.id}?type=product`);
                  }}
                  className="w-1/2 bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc] hover:from-[#162e6f] hover:to-[#2f47c4] text-white py-2 rounded-xl text-xs sm:text-sm font-semibold transition flex items-center justify-center group-hover:scale-[1.02]"
                >
                  <span className="sm:hidden">Buy</span>
                  <span className="hidden sm:inline">Buy Now</span>
                </button>

                <a
                  href={getProductEnquiryLink(p, { type: "product" })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-1/2 border-2 border-[#1F3C88] text-[#1F3C88] hover:bg-[#1F3C88] hover:text-white py-2 rounded-xl text-xs sm:text-sm font-semibold transition flex items-center justify-center gap-2"
                >
                  <span className="sm:hidden">Enquiry</span>
                  <span className="hidden sm:inline">Enquiry Now</span>
                </a>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>

  {showToast && (
    <div className="fixed bottom-6 right-6 z-50">
      <div className="bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc] text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3">
        <span className="text-sm font-semibold">{toastMsg}</span>
      </div>
    </div>
  )}
</section>

  );
};

export default NewArrival;
